import type { SensorRecord } from "@/api/types";
import {
  formatDateShort,
  formatIsoTimestampMst,
  isIsoTimestamp,
} from "@/lib/format-date";
import { CATEGORIES } from "@/pages/help/catalog";

const PREVIEW_VALUE_MAX = 28;
const PREVIEW_TOTAL_MAX = 120;

type TemporalFieldType = "timestamp" | "epoch ms";

export const HEALTH_CONNECT_SUMMARY_COLUMNS = [
  "date",
  "steps",
  "distance",
  "active_calories",
  "total_calories",
  "heart_rate",
  "resting_heart_rate",
  "respiratory_rate",
  "oxygen_saturation",
  "sleep_sessions",
  "weight",
  "body_fat",
] as const;

const TEMPORAL_FIELDS = new Map<string, TemporalFieldType>();

for (const category of CATEGORIES) {
  for (const source of category.sources) {
    for (const field of source.fields) {
      if (field.type === "timestamp" || field.type === "epoch ms") {
        TEMPORAL_FIELDS.set(`${source.source}\0${field.name}`, field.type);
      }
    }
  }
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + "…";
}

function readNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function readObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function formatNumber(value: unknown, maximumFractionDigits = 1): string | null {
  const number = readNumber(value);
  if (number === null) return null;
  return new Intl.NumberFormat("en-US", { maximumFractionDigits }).format(number);
}

function formatDistance(value: unknown): string | null {
  const object = readObject(value);
  const meters = readNumber(object?.total_meters ?? value);
  if (meters === null) return null;
  if (meters >= 1000) return `${formatNumber(meters / 1000, 2)} km`;
  return `${formatNumber(meters, 0)} m`;
}

function formatBpmRange(value: unknown): string | null {
  const object = readObject(value);
  if (!object) return null;

  const avg = formatNumber(object.avg_bpm, 0);
  const min = formatNumber(object.min_bpm, 0);
  const max = formatNumber(object.max_bpm, 0);
  if (avg && min && max) return `${avg} avg · ${min}-${max} bpm`;
  if (avg) return `${avg} avg bpm`;
  return null;
}

function formatPercentRange(value: unknown): string | null {
  const object = readObject(value);
  if (!object) return null;

  const avg = formatNumber(object.avg_percent, 0);
  const min = formatNumber(object.min_percent, 0);
  const max = formatNumber(object.max_percent, 0);
  if (avg && min && max) return `${avg}% avg · ${min}-${max}%`;
  if (avg) return `${avg}% avg`;
  return null;
}

function formatRespiratoryRate(value: unknown): string | null {
  const object = readObject(value);
  if (!object) return null;

  const avg = formatNumber(object.avg_rate, 1);
  if (!avg) return null;
  const sampleCount = formatNumber(object.sample_count, 0);
  return sampleCount ? `${avg}/min · ${sampleCount} sample${sampleCount === "1" ? "" : "s"}` : `${avg}/min`;
}

function formatWeight(value: unknown): string | null {
  const object = readObject(value);
  const kg = readNumber(object?.kg ?? value);
  if (kg === null) return null;
  return `${formatNumber(kg, 1)} kg`;
}

function formatBodyFat(value: unknown): string | null {
  const object = readObject(value);
  const percentage = readNumber(object?.percentage ?? value);
  if (percentage === null) return null;
  return `${formatNumber(percentage, 1)}%`;
}

function formatTimeRangeMst(start: unknown, end: unknown): string | null {
  if (typeof start !== "string" || typeof end !== "string") return null;
  if (!isIsoTimestamp(start) || !isIsoTimestamp(end)) return null;

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Phoenix",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
  return `${formatter.format(new Date(start))}-${formatter.format(new Date(end))}`;
}

function formatDuration(start: unknown, end: unknown): string | null {
  if (typeof start !== "string" || typeof end !== "string") return null;
  const minutes = Math.round((Date.parse(end) - Date.parse(start)) / 60_000);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours && remainder) return `${hours}h ${remainder}m`;
  if (hours) return `${hours}h`;
  return `${remainder}m`;
}

function formatSleepSessions(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0) return null;

  const first = readObject(value[0]);
  const start = first?.start_time;
  const end = first?.end_time;
  const range = formatTimeRangeMst(start, end);
  const duration = formatDuration(start, end);
  const count = `${value.length} session${value.length === 1 ? "" : "s"}`;

  if (duration && range) return `${duration} · ${range}`;
  if (duration) return `${duration} · ${count}`;
  if (range) return `${count} · ${range}`;
  return count;
}

export function formatValuesPreview(values: Record<string, unknown>): string {
  const parts: string[] = [];
  let total = 0;
  for (const [k, v] of Object.entries(values)) {
    if (v === null || v === undefined || v === "") continue;
    const part = `${k}=${truncate(stringify(v), PREVIEW_VALUE_MAX)}`;
    if (total + part.length > PREVIEW_TOTAL_MAX && parts.length > 0) {
      parts.push("…");
      break;
    }
    parts.push(part);
    total += part.length + 3;
  }
  return parts.length ? parts.join(" · ") : "{}";
}

const HIDDEN_SOURCE_FIELDS: Record<string, ReadonlySet<string>> = {
  AT_LOCATION: new Set(["activity_before"]),
};

export function getVisibleRecordValues(
  record: Pick<SensorRecord, "source" | "values"> | null | undefined,
): Record<string, unknown> {
  const values = record?.values ?? {};
  const hidden = HIDDEN_SOURCE_FIELDS[record?.source?.toUpperCase() ?? ""];
  if (!hidden) return values;
  return Object.fromEntries(
    Object.entries(values).filter(([key]) => !hidden.has(key)),
  );
}

export function discoverValueColumns(records: SensorRecord[]): string[] {
  const keys = new Set<string>();
  for (const r of records) {
    for (const k of Object.keys(getVisibleRecordValues(r))) keys.add(k);
  }
  return Array.from(keys).sort();
}

function formatTemporalValue(value: unknown, type?: TemporalFieldType): string | null {
  let timestamp: number | null = null;

  if (type === "epoch ms") {
    if (typeof value === "number" && Number.isFinite(value)) {
      timestamp = value;
    } else if (typeof value === "string" && /^-?\d+$/.test(value.trim())) {
      timestamp = Number(value);
    }
  }

  if (timestamp === null && typeof value === "string" && isIsoTimestamp(value)) {
    timestamp = Date.parse(value);
  }

  if (timestamp === null || !Number.isFinite(timestamp)) return null;

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  return formatDateShort(date.toISOString());
}

export function formatHealthConnectJsonValue(value: unknown): unknown {
  if (typeof value === "string") {
    return isIsoTimestamp(value) ? formatIsoTimestampMst(value) : value;
  }

  if (Array.isArray(value)) {
    return value.map(formatHealthConnectJsonValue);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, val]) => [
        key,
        formatHealthConnectJsonValue(val),
      ]),
    );
  }

  return value;
}

export function formatHealthConnectSummaryCell(column: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";

  if (column === "steps") return formatNumber(value, 0) ?? String(value);
  if (column === "distance") return formatDistance(value) ?? String(value);
  if (column === "active_calories") return `${formatNumber(value, 0) ?? String(value)} kcal`;
  if (column === "total_calories") return `${formatNumber(value, 0) ?? String(value)} kcal`;
  if (column === "heart_rate") return formatBpmRange(value) ?? formatCellValue("HEALTH_CONNECT", column, value);
  if (column === "resting_heart_rate") return `${formatNumber(value, 0) ?? String(value)} bpm`;
  if (column === "respiratory_rate") {
    return formatRespiratoryRate(value) ?? formatCellValue("HEALTH_CONNECT", column, value);
  }
  if (column === "oxygen_saturation") {
    return formatPercentRange(value) ?? formatCellValue("HEALTH_CONNECT", column, value);
  }
  if (column === "sleep_sessions") {
    return formatSleepSessions(value) ?? formatCellValue("HEALTH_CONNECT", column, value);
  }
  if (column === "weight") return formatWeight(value) ?? formatCellValue("HEALTH_CONNECT", column, value);
  if (column === "body_fat") return formatBodyFat(value) ?? formatCellValue("HEALTH_CONNECT", column, value);

  return formatCellValue("HEALTH_CONNECT", column, value);
}

export function formatCellValue(source: string, column: string, value: unknown): string {
  if (value === null || value === undefined) return "—";

  const temporalType = TEMPORAL_FIELDS.get(`${source.toUpperCase()}\0${column}`);
  const formattedTimestamp = formatTemporalValue(value, temporalType);
  if (formattedTimestamp !== null) return formattedTimestamp;

  if (source.toUpperCase() === "HEALTH_CONNECT") {
    value = formatHealthConnectJsonValue(value);
  }

  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return truncate(stringify(value), 60);
}

export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}
